import { Component, ViewChild, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, NavigationEnd, RouterEvent, Scroll } from '@angular/router';

// ionic
import { AlertController, IonContent, ModalController } from '@ionic/angular';
import { PopoverController, Platform } from '@ionic/angular';
import { Events } from "../../helpers/events";

// other libraries
import { LangChangeEvent } from "@ngx-translate/core";

// rxjs
import { Subject, Subscription } from "rxjs";
import { debounceTime, filter, take, takeUntil } from "rxjs/operators";

// components
import { AttendeeFilterComponent } from "../../components/attendee-filter/attendee-filter.component";
import { EventDetailComponent } from "../../components/event-detail/event-detail.component";
import { NotificationsComponent } from "../../components/notifications/notifications.component";
import { CommunityFilterComponent } from "../../components/community-filter/community-filter.component";
import { AddNewsfeedComponent } from "../../components/add-newsfeed/add-newsfeed.component";
import { NewsfeedDetailComponent } from '../../components/newsfeed-detail/newsfeed-detail.component';
import { NewsfeedListComponent } from "../../components/newsfeed-list/newsfeed-list.component";
import { StreamListComponent } from "../../components/stream-list/stream-list.component";
import { MarketplaceListComponent } from "../../components/marketplace-list/marketplace-list.component";

// services
import { EventService } from "../../services/event.service";
import { ParticipantService } from "../../services/participant.service";
import { UserService } from "../../services/user.service";
import { PaginatorService } from "../../services/paginator.service";
import { NewsfeedService } from "../../services/newsfeed.service";
import { MarketplaceService } from "../../services/marketplace.service";

// models
import { Appointment } from "../../models/appointment";
import { Container } from "../../models/container";
import { Event } from "../../models/event";
import { Feed } from "../../models/feed";
import { Group } from "../../models/group";
import { Keyword } from "../../models/keyword";
import { Participant } from "../../models/participant";
import { ParticipantTimeslot } from "../../models/participant-timeslot";
import { Newsfeed } from "../../models/newsfeed";

import { log } from "../../helpers/log";

import { getThemeConfig } from '../config';

// extends
import { BasePage } from "../base";
import { Marketplace } from 'src/app/models/marketplace';

@Component({
    selector: 'app-dashboard',
    templateUrl: './dashboard.page.html',
    styleUrls: ['./dashboard.page.scss'],
})

export class DashboardPage extends BasePage implements OnInit, OnDestroy {

    @ViewChild(IonContent) content: IonContent;
    @ViewChild(NewsfeedListComponent) newsfeedListComponent: NewsfeedListComponent;
    @ViewChild(StreamListComponent) streamListComponent: StreamListComponent;
    @ViewChild(AddNewsfeedComponent) addNewsfeedComponent: AddNewsfeedComponent;

    @ViewChild(MarketplaceListComponent) public marketplaceList: MarketplaceListComponent;

    /**
     * unsubscribe subject
     *
     * @type {Subject<void>}
     */
    private ngUnsubscribe: Subject<void> = new Subject<void>();

    /**
     * subscription for event changes subject
     *
     * @type {Subscription}
     */
    private subscriptionEvent: Subscription;

    /**
     * subscription for route changes subject
     *
     * @type {Subscription}
     */
    private subscription: Subscription;

    /**
     * Suggested keywords
     *
     * @type Keyword[]
     */
    public suggestedKeywords: Keyword[] = [];

    /**
     * Suggested keywords by others
     *
     * @type Keyword[]
     */
    public suggestedKeywordsByOthers: Keyword[] = [];

    /**
     * Existing keywords
     *
     * @type Keyword[]
     */
    public existingKeywords: Keyword[] = [];

    /**
     * bookmarks filter
     *
     * @type {boolean}
     */
    public bookmarks: boolean = false;

    /**
     * likes filter
     *
     * @type {boolean}
     */
    public likes: boolean = false;

    /**
     * order filter
     *
     * @type {string}
     */
    public order: string = 'matching_factor';

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public loading: boolean = true;

    /**
     * initial loading state
     *
     * @type {boolean}
     */
    public dataLoading: boolean = false;

    /**
     * status of dropdown boxex
     *
     * @type {boolean}
     */
    public appointmentBox: boolean = false;
    public notificationBox: boolean = false;

    /**
     * selected segment
     *
     * @type {String}
     */
    public segment: string = 'info';

    /**
     * selected view
     *
     * @type {String}
     */
    public view: string = 'matching';

    /**
     * object with block list
     *
     * @type {}
     */
    public blocks: any[];

    /**
     * participant has some keyword setting
     *
     * @type {boolean}
     */
    public savedKeywords: boolean = false;

    /**
     * participant has some keyword setting
     *
     * @type {boolean}
     */
    public visibleAllKeywords: boolean = false;

    /**
     * visible participants
     *
     * @type Participant
     */
    public visibleAttendees: Participant[] = [];

    /**
     * search string for attendees list
     *
     * @type {String}
     */
    public attendeesSearch: string = '';

    /**
     * search string for attendees address
     *
     * @type {String}
     */
    public selectedAddress: Object = null;

    /**
     * search distance for attendees address
     *
     * @type {String}
     */
    public selectedDistance: number = 20;

    /**
     * how many appointments should be visible on dashboard
     *
     * @type Participant
     */
    public appointmentsLimit: number = 3;

    /**
     * height of the iframe
     *
     * @type string
     */
    public frameHeight: number = 800;

    attendeesTotal: number = 0;
    attendeesLimit: number = 40;
    attendeesOffset: number = 0;

    attendeesLimitValues: { value: number, name: string }[];

    pager: any;

    page: number = 1;

    public homeUrlSegment: string = 'home';

    /**
     * if we should show header, after some scroll yes...
     *
     * @type {boolean}
     */
    public showHeader: boolean = false;

    /**
     * disable preview behaviour
     *
     * @type {boolean}
     */
    public isPreview: boolean = false;

    public viewType: string = 'lines';

    public containers: Container[] = [];

    // handlers for events
    filterHandler;
    attendeeDetailHandler;
    attendeeShowHandler;
    eventChangeHandler;
    newsfeedShowHandler;
    marketplacePostShowHandler;
    votingShowHandler;
    videoShowHandler;

    /**
     * disable preview behaviour
     *
     * @type {boolean}
     */
    public kyewordsEmpty: boolean = true;

    /**
     * info for user about live calculation
     *
     * @type {boolean}
     */
    public calculatingCalls: number = 0;

    public scrollTop: number = 0;

    public countSizes = {
        2: [1, 2],
        3: [1, 2, 3],
        4: [1, 2, 3, 4],
        5: [1, 2, 3, 4, 5]
    };

    public selectedKeywordIds: any[] = [];
    public selectedCounties: any[] = [];

    /**
     * disable preview behaviour
     *
     * @type {boolean}
     */
    public keywordsEmpty: boolean = true;

    showSharing: boolean = false;

    /**
     * which select type should be used.. alert/popover/action-sheet
     *
     * @type {string}
     */
    public selectType: string = 'popover';

    /**
     * currently selected tab
     *
     * @type boolean
     */
    public selectedAttendeesTabId: number = 0;

    /**
     * blocks feed detail to be opened multiple times
     *
     * @type boolean
     */
    public feedDetailModalOpened: boolean = false;

    /**
     * is matching visible
     *
     * @type boolean
     */
    public matchingOrderAllowed: boolean = true;

    /**
     * is filter applied
     *
     * @type boolean
     */
    public filtered: boolean = false;

    /**
     * is filter applied
     *
     * @type boolean
     */
    public showSearchForm: boolean = true;

    /**
     * is limited view for member list
     *
     * @type boolean
     */
    public limitedMemberList: boolean = false;

    /**
     * selected marketplace to show
     *
     * @type Marketplace
     */
    public selectedMarketplace: Marketplace;

    /**
     * constructor
     *
     * @param translate
     * @param eventService
     * @param userService
     * @param participantService
     */
    constructor(
        public appEvents: Events,
        public route: ActivatedRoute,
        public platform: Platform,
        public eventService: EventService,
        public userService: UserService,
        public participantService: ParticipantService,
        public popoverController: PopoverController,
        public alertCtrl: AlertController,
        public paginatorService: PaginatorService,
        public modalController: ModalController,
        public newsfeedService: NewsfeedService,
        public marketplaceService: MarketplaceService
    ) {
        super();
        // user language
        this.userLang = this.translate.currentLang;

        if (this.plt.sizeSm) {
            this.selectType = 'action-sheet';
        }

        this.segment = this.route.snapshot.paramMap.get('segment');
        this.view = this.route.snapshot.paramMap.get('view');

        this.viewType = !this.plt.sizeSm ? 'grid' : 'lines';


        // if there is no navigation history, navigate to default page
        if (this.routerExtService.emptyHistory()) {
            this.routerExtService.softNavigate(this.plt.defaultLink + '/home/' + this.segment + '/' + (this.view ? this.view : ''));
        }
    }

    /**
     * on init
     *
     * @return void
     */
    ngOnInit() {

        if (this.plt.isConnected) {
            this.init();
        }
        window.addEventListener('apponline', () => {
            this.init();
        }, { once: true });
    }

    init() {
        this.filterHandler = (data) => {
            // this.afterFilterChanged(data);
        }

        this.attendeeDetailHandler = (attendee) => {
            // find attendee in actual list and change his data
            this.visibleAttendees.forEach((item) => {
                if (item.id == attendee.id) {
                    // update data for attendee
                    item.liked = attendee.liked;
                    item.bookmarked = attendee.bookmarked;
                }
            });
            // reload view data
            // this.refresh();
        }

        this.attendeeShowHandler = (attendeeId) => {
            // show modal with attendee
            if (!this.plt.attendeeDetailOpened) {
                this.plt.attendeeDetailOpened = true;
                this.eventService.getCurrentEvent().pipe(
                    take(1),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        this.event = event;
                        this.showAttendeeDetail(null, false, false, attendeeId);
                    });
            }
        }

        this.newsfeedShowHandler = (newsfeedId) => {
            // show modal with newsfeed
            if (!this.plt.newsfeedOpened) {
                this.plt.newsfeedOpened = true;
                this.eventService.getCurrentEvent().pipe(
                    take(1),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        this.event = event;
                        this.showNewsfeedDetail(Number(newsfeedId));
                    });
            }
        }

        this.marketplacePostShowHandler = (marketplacePostId, marketplaceId) => {
            // show modal with newsfeed
            if (!this.plt.marketplacePostOpened) {
                this.plt.marketplacePostOpened = true;
                this.eventService.getCurrentEvent().pipe(
                    take(1),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        this.event = event;
                        this.showMarketplacePostDetail(parseInt(marketplacePostId), parseInt(marketplaceId), false);
                    });
            }
        }

        this.votingShowHandler = (votingId) => {
            // show modal with newsfeed
            if (!this.plt.votingOpened) {
                this.plt.votingOpened = true;
                this.eventService.getCurrentEvent().pipe(
                    take(1),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        this.event = event;
                        this.showLiveVoting(votingId, false);
                    });
            }
        }

        this.videoShowHandler = (videoLink: string, direct: boolean = false) => {
            // show modal with video
            if (!this.plt.videoOpened) {
                this.plt.videoOpened = true;
                this.showVideo(videoLink, direct);
            }
        }

        this.eventChangeHandler = () => {
            this.resetAttendeeForm();
            this.attachEvents();
            this.selectedAttendeesTabId = 0;
            this.plt.activeSubscriptions = [];
            // this.refresh();
        }

        this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
            this.userLang = event.lang;
            this.setAttendeesLimitValues();
        });

        // subscribe to current user
        this.userService.getCurrentUser().pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0)
        ).subscribe((user) => {
            if (user.id) {
                this.user = user;

                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }

                this.subscriptionEvent = this.eventService.getCurrentEvent().pipe(
                    takeUntil(this.ngUnsubscribe),
                    debounceTime(0)
                ).subscribe(
                    (event) => {
                        if (this.user && this.user.selected_participant) {
                            this.user.selected_participant.event_id = event.id;
                            this.user.selected_participant.event = event;

                            // secure that links to marketplace on events without marketplace will work properly
                            if (this.segment == 'marketplace') {
                                if (event.marketplaces.length == 0) {
                                    this.segment = 'info';
                                    this.view = 'matching';
                                    // navigate to dashboard, because there is no marketplace in this event
                                    this.router.navigate([this.plt.defaultLink + '/home/info/matching']);
                                }
                            }
                        }

                        this.event = event;
                        this.themeConfig = getThemeConfig(this.event.id);

                        if (this.env.limitedMemberList[this.env.hostname]?.communities.includes(this.event.id)) {
                            this.limitedMemberList = true;
                            this.order = 'search';
                        } else {
                            this.limitedMemberList = false;
                            this.order = 'matching_factor';
                        }

                        // update event if changed
                        if (!this.plt.dashboardInit && (!this.limitedMemberList || this.segment != 'attendees')) {
                            this.refresh();
                        }

                        this.checkDeeplink();
                    });
                this.plt.activeSubscriptions.push(this.subscriptionEvent);

            } else {
                this.user = null;
                this.event = null;
                // cancel previous subscription
                if (this.subscriptionEvent) {
                    this.subscriptionEvent.unsubscribe();
                }
            }
        });

        // wait until first redirects are finished after app is properly loaded, to avoid duplicit calls
        // setTimeout(() => {
        this.subscription = this.router.events.pipe(
            takeUntil(this.ngUnsubscribe),
            debounceTime(0),
            // filter((e: RouteEvent | RouterEvent): e is RouterEvent => e instanceof RouterEvent)
        ).subscribe(
            (navigationEvent: any) => {

                // for route event event get route event
                if ((navigationEvent instanceof Scroll)) {
                    navigationEvent = <RouterEvent>navigationEvent.routerEvent;
                }

                // using url from NavigationEnd instance, because route.snapshot was not sometimes updated...
                if (navigationEvent instanceof RouterEvent && navigationEvent instanceof NavigationEnd) {

                    let url = navigationEvent.url;

                    // load current proper event and user
                    this.userService.getCurrentUser().pipe(
                        take(1),
                        debounceTime(0)
                    ).subscribe((user) => {
                        if (user.id) {
                            this.user = user;

                            this.subscriptionEvent = this.eventService.getCurrentEvent().pipe(
                                take(1),
                                debounceTime(0)
                            ).subscribe(
                                (event) => {
                                    this.event = event;

                                    if (this.env.limitedMemberList[this.env.hostname]?.communities.includes(this.event.id)) {
                                        this.limitedMemberList = true;
                                        this.order = 'search';
                                    } else {
                                        this.limitedMemberList = false;
                                        this.order = 'matching_factor';
                                    }

                                    let params = url.replace(this.plt.defaultLink + '/home/', '').split('/');

                                    if (params[0] && params[1] && (params[0] == 'info' || params[0] == 'marketplace' || params[0] == 'attendees' || params[0] == 'detail')) {
                                        if (params[0] == 'info' && this.user && this.user.selected_participant) {
                                            this.savedKeywords = !!this.user.selected_participant.keywords.length
                                        }

                                        // secure that links to marketplace on events without marketplace will work properly
                                        if (this.segment == 'marketplace') {
                                            if (this.event && this.user && (this.event && this.event.marketplaces.length == 0)) {
                                                this.segment = 'info';
                                                this.view = 'matching';
                                                // navigate to dashboard, because there is no marketplace in this event
                                                this.router.navigate([this.plt.defaultLink + '/home/info/matching']);
                                            }
                                        }

                                        this.plt.hideBottomMenu = false;

                                        this.segment = params[0];
                                        // view param
                                        this.view = params[1];
                                        // reset filters
                                        if (this.segment == 'attendees') {
                                            this.resetAttendeeForm();
                                            this.attachEvents();
                                        }
                                        // refresh page content
                                        this.refresh();

                                        // scroll to top
                                        this.content.scrollToTop(0);

                                        this.checkDeeplink();

                                    }
                                });
                        }
                    });
                }
            });
        // }, 500);

        // attach hooks on event change
        this.appEvents.subscribe('event:changed', this.eventChangeHandler);

        window.addEventListener('apponline', () => {
            this.refresh();
        });

        this.eventService.getCurrentEvent().pipe(
            take(1)
        ).subscribe(
            (event) => {
                this.event = event;
                this.attachEvents();
            });
    }

    checkDeeplink() {
        // console.info('CheckDeeplinks', window.location.search)
        let urlParams = new URLSearchParams(window.location.search);
        let attendeeId = urlParams.get('profile_id');
        if (attendeeId) {
            if (!this.plt.attendeeDetailOpened) {
                this.plt.attendeeDetailOpened = true;
                this.showAttendeeDetail(null, false, false, attendeeId);
            }
        }

        if (this.event.marketplaces.length > 0) {
            this.selectedMarketplace = this.event.marketplaces[0];
        }

        let marketplaceId = urlParams.get('marketplace_id');

        if (marketplaceId && this.event.marketplaces.length > 0) {
            let marketplace;
            if (marketplace = this.event.marketplaces.filter(m => m.id.toString() == marketplaceId)) {
                this.selectedMarketplace = marketplace[0];
                console.info(this.selectedMarketplace);
            }
        }


        let marketplacePostId = urlParams.get('post_id');
        if (marketplaceId && marketplacePostId) {
            if (!this.plt.marketplacePostOpened) {
                this.plt.marketplacePostOpened = true;
                this.showMarketplacePostDetail(parseInt(marketplacePostId), parseInt(marketplaceId), false, !!urlParams.get('showComments'));
            }
        }

        let votingId = urlParams.get('voting_id');
        if (votingId) {
            if (!this.plt.votingOpened) {
                this.plt.votingOpened = true;
                this.showLiveVoting(votingId, false);
            }
        }


        let newsfeedId = urlParams.get('newsfeed_id');
        if (newsfeedId) {
            if (!this.plt.newsfeedOpened) {
                this.plt.newsfeedOpened = true;
                this.showNewsfeedDetail(Number(newsfeedId));
            }
        }

        let videoLink = urlParams.get('video_link');
        if (videoLink) {
            if (!this.plt.newsfeedOpened) {
                this.plt.newsfeedOpened = true;
                this.showVideo(videoLink);
            }
        }
    }

    afterFilterChanged(data) {
        this.likes = data.likes;
        this.bookmarks = data.bookmarks;
        this.order = data.order;
        this.attendeesOffset = 0;
        this.page = 1;
        // reset member list in case of limited view
        if (this.limitedMemberList) {
            this.visibleAttendees = [];

            // if filter set, apply filtering for limited view
            if (this.bookmarks || this.likes) {
                this.filtered = true;
                this.showSearchForm = false;
                this.refreshAttendees();
            } else {
                // otherwise keep filter
                this.showSearchForm = true;
                this.applyFilter({
                    attendeesSearch: this.attendeesSearch,
                    selectedKeywordIds: this.selectedKeywordIds,
                    selectedCounties: this.selectedCounties,
                    selectedAddress: this.selectedAddress,
                    selectedDistance: this.selectedDistance
                });
            }
        } else {
            this.refreshAttendees();
        }

    }

    attachEvents() {
        // remove previous subscription

        this.appEvents.unsubscribe('attendee:filter');
        this.appEvents.unsubscribe('attendee:changed');
        this.appEvents.unsubscribe('attendee:show');
        this.appEvents.unsubscribe('newsfeed:show');

        // sync state of filter from component
        this.appEvents.subscribe('attendee:filter', this.filterHandler);

        // detect changes on attendee, like bookmark/like in popup and sycn state
        this.appEvents.subscribe('attendee:changed', this.attendeeDetailHandler);

        // show attendee modal if requested
        this.appEvents.subscribe('attendee:show', this.attendeeShowHandler);

        // show newsfeed modal if requested
        this.appEvents.subscribe('newsfeed:show', this.newsfeedShowHandler);

        this.appEvents.subscribe('marketplace:post:show', this.marketplacePostShowHandler);

        this.appEvents.subscribe('voting:show', this.votingShowHandler);

        this.appEvents.subscribe('video:show', this.videoShowHandler);

    }

    /**
     * after view init
     *
     * @return void
     */
    ngAfterViewInit() {
        // we are lisening to router changes
        // if (this.user && this.user.id) {
        //     this.refresh();
        // }
    }

    /**
     * refresh page content
     *
     * @return void
     */
    public refresh($event?) {

        // in case of no connection, just disable
        if (!this.plt.isConnected) {
            return;
        }

        this.plt.dashboardInit = true;

        switch (this.segment) {
            case 'info':
                this.refreshDashboard($event);
                // refresh newsfeed properly
                if (this.event && this.event.is_homepage_content_newsfeed && this.newsfeedListComponent) {
                    this.newsfeedListComponent.refresh($event);
                }
                if (this.event && this.event.is_homepage_content_stream && this.streamListComponent) {
                    this.streamListComponent.refresh($event);
                }
                break;
            case 'detail':
                this.refreshDashboard($event);
                break;
            case 'attendees':
                if (this.limitedMemberList) {
                    if (!this.filtered) {
                        this.visibleAttendees = [];
                    }

                    if ($event) {
                        $event.target.complete();
                    } else {
                        this.loading = false;
                        this.dataLoading = false;
                    }

                    // if filter set, apply filtering for limited view
                    if (this.bookmarks || this.likes) {
                        this.filtered = true;
                        this.showSearchForm = false;
                        this.refreshAttendees();
                    } else {
                        // otherwise keep filter
                        this.showSearchForm = true;
                        this.applyFilter({
                            attendeesSearch: this.attendeesSearch,
                            selectedKeywordIds: this.selectedKeywordIds,
                            selectedCounties: this.selectedCounties,
                            selectedAddress: this.selectedAddress,
                            selectedDistance: this.selectedDistance
                        });
                    }
                } else {
                    this.refreshAttendees();
                }
                break;
            case 'marketplace':
                if ($event) {
                    $event.target.complete();
                } else {
                    this.loading = false;
                    this.dataLoading = false;
                }
                if (this.marketplaceList) {
                    // this.marketplaceList.refresh();
                }
                break;

        }
    }

    /**
     * refresh dashboard
     *
     * @return void
     */
    public refreshDashboard($event?) {

        // NOTE[jg] this seems not to be needed anymore as subscription was fixed in general
        if (this.plt.eventInChange) {
            return;
        }

        // Be sure to load alway latest event
        this.eventService.getCurrentEvent().pipe(
            take(1)
        ).subscribe(
            (event) => {

                // update event if changed
                if (this.event && this.event.updated_at != event.updated_at) {
                    this.eventService.setCurrentEvent(event);
                }

                this.event = event;

                if (this.event && this.event.id) {
                    let subscription = this.eventService.getById(this.event.id).subscribe((event: Event) => {

                        this.setupEvent(event);

                        // update event in current user to keep live chagnes...
                        this.user.selected_participant.event = this.event;

                        this.userService.setCurrentUser(this.user);

                        setTimeout(() => {
                            // just check if user is still logged in... reset not visited feeds only if the box is visited on dashboard
                            if (localStorage.getItem('token') && (event.is_homepage_content_matching || !this.plt.tabMenu)) {
                                this.participantService.setFeedsVisited().subscribe();
                            }
                        }, 2000);

                        // load most matching participants
                        if (this.user) {
                            if (!!this.user.selected_participant.keywords.length) {

                            } else {
                                // redirect to managing keyword if not
                                if (this.savedKeywords && this.segment == 'info') {
                                    this.savedKeywords = false;
                                    this.routerExtService.softNavigate(this.plt.defaultLink + '/home/info/manage-keywords', true);
                                }
                            }
                        }

                        setTimeout(() => {
                            // hide loading
                            if ($event) {
                                $event.target.complete();
                            } else {
                                this.loading = false;
                                this.dataLoading = false;
                            }
                        }, 100);
                    },
                        (error) => {
                            // TODO
                            log('error', 'refreshDashboard', error);
                            // Sentry.captureException(error);
                            // this.showError(error);
                            // hide loading
                            if ($event) {
                                $event.target.complete();
                            } else {
                                this.loading = false;
                                this.dataLoading = false;
                            }
                        });

                    this.plt.activeSubscriptions.push(subscription);
                } else {
                    // hide loading
                    if ($event) {
                        $event.target.complete();
                    } else {
                        this.loading = false;
                        this.dataLoading = false;
                    }
                }
            });
    }

    /**
     * setup event
     *
     * @param {Event} event
     */
    private setupEvent(event: Event) {

        // copy last selected group for this event...
        if (this.event) {
            if (this.event.id == event.id) {
                event.selectedGroup = this.event.selectedGroup;
                event.selectedAttendeesTab = this.event.selectedAttendeesTab;
            }
        }

        // update dashboard feeds from event if there are any
        if (event.dashboardFeeds) {
            this.plt.setDashboardFeeds(event.dashboardFeeds);
        }

        this.event = new Event(event, this.user);
        if (this.selectedAttendeesTabId == 0) {
            if (this.plt.sizeSm) {
                this.event.selectedAttendeesTab = null;
            }
            else if (this.event.selectedAttendeesTab) {
                this.selectedAttendeesTabId = this.event.selectedAttendeesTab.id;
            }
        }
        this.setupContainers();

        // NOTE[jg] - now it seems useless to trigger this twice
        // this.eventService.setCurrentEvent(this.event);

        // show only appoitments with both participants
        if (this.event.appointments) {
            this.event.appointments = this.event.appointments.filter((appointment: Appointment) => {
                return appointment.participants && appointment.participants.length > 1;
            });
        }

        // add personal appointments
        if (this.user && this.user.selected_participant_id && this.segment == 'info' && !this.plt.sizeSm) {
            let subscription = this.participantService.getTimeslot(this.user.selected_participant.id).subscribe((data) => {
                if (data['participanTimeslots'] && data['participanTimeslots'].hasOwnProperty('id')) {
                    let participantTimeslot = new ParticipantTimeslot(data['participanTimeslots']);
                    let privateAppointments = participantTimeslot.generatePrivateAppointment().filter((appointment) => {
                        return appointment.starts_at;
                    });

                    privateAppointments.forEach((privateAppointment) => {
                        let appointment = new Appointment(privateAppointment);

                        if (!appointment.past) {
                            this.event.appointments.push(appointment);
                        }
                    });

                    // sort appointments
                    if (this.event.appointments) {
                        this.event.appointments = this.event.appointments.sort(function (a, b) {
                            return a.starts_at > b.starts_at ? 1 : -1;
                        });
                    }
                }
            });
            this.plt.activeSubscriptions.push(subscription);
        }

        this.initBlocks();

        // containers (and keywords)
        if (this.user && this.event.containers) {

            //TODO: new
            // this.setupContainers();

            if (this.view != 'manage-keywords') {
                this.savedKeywords = !!this.user.selected_participant.keywords.length;
            }
        }
    }

    private setupContainers() {
        this.suggestedKeywords = [];
        this.containers = [];
        this.keywordsEmpty = true;

        for (let c = 0; c < this.event.containers.length; c++) {
            const container = new Container(this.event.containers[c]);
            this.event.containers[c] = container;
            let keywordIncluded = false;
            // keywords
            if (container.keywords) {
                for (let k = 0; k < container.keywords.length; k++) {
                    const keyword = container.keywords[k];
                    this.keywordsEmpty = false;

                    if (this.user && this.user.selected_participant && this.user.selected_participant.keywords.length
                        && this.user.selected_participant.keywords.find((item) => { return keyword.id == item.id })) {
                        keywordIncluded = true;
                    }

                    // suggested keywords
                    if (this.user && keyword.participant_id === this.user.selected_participant_id && !keyword.approved_at) {
                        this.suggestedKeywords.push(keyword);
                    }
                    // suggested keywords by others
                    else if (this.user && keyword.participant_id !== this.user.selected_participant_id && !keyword.approved_at) {
                        this.suggestedKeywordsByOthers.push(keyword);
                    }
                    // existing keywords
                    else {
                        this.existingKeywords.push(keyword);
                    }
                }
            }

            // order keywords
            container.orderKeywordsByName(this.userLang, this.event.default_locale);

            // check if some keyword is checked, then include it in visible list
            if (keywordIncluded) {
                this.containers.push(container);
            }
        }
    }

    /**
     * show details about feed notification
     *
     * @param {Feed} feed
     */
    public openFeedDetail(feed: Feed): void {
        switch (feed.data.type) {
            case 'like':
            case 'visit':
                this.participantService.getById(+feed.data.participant_id).subscribe(
                    (participant) => {
                        this.showAttendeeDetail(participant);
                    });
                break;
            case 'message':
                this.router.navigate([this.plt.defaultLink + '/messages/direct', feed.data.participant_id]);
                break;
            case 'newsfeed-comment':
            case 'newsfeed-like':
            case 'newsfeed-confirm':
            case 'newsfeed-reject':
                this.showNewsfeedDetail(feed.data.newsfeed_id);
                break;
            case 'marketplace-post-waiting':
            case 'marketplace-post-created':
            case 'marketplace-post-comment':
            case 'marketplace-post-like':
            case 'marketplace-post-confirm':
            case 'marketplace-post-reject':
            case 'marketplace-post-category-deleted':
            case 'marketplace-post-visit':
            case 'marketplace-post-contact-removed':
            case 'marketplace-post-contact-added':
                this.showMarketplacePostDetail(feed.data.post_id, feed.data.marketplace_id);
                break;
        }
    }

    /**
     * refresh attendees list
     *
     * @return void
     */
    public refreshAttendees($event?, reset = true) {
        this.eventService.getCurrentEvent().pipe(
            take(1)
        ).subscribe(
            (event) => {

                this.event = event;

                this.setupEvent(event);
                this.setAttendeesLimitValues();

                if (this.plt.sizeSm && reset) {
                    this.attendeesOffset = 0;
                    this.page = 1;
                }

                this.dataLoading = true;
                this.matchingOrderAllowed = true;

                this.participantService.getFilteredListByEvent(
                    this.event.id,
                    this.attendeesLimit,
                    this.attendeesOffset,
                    this.attendeesSearch,
                    {
                        likes: this.likes,
                        bookmarks: this.bookmarks,
                        attendeesTabId: this.selectedAttendeesTabId ? this.selectedAttendeesTabId : null,
                        keywordIds: this.selectedKeywordIds,
                        counties: this.selectedCounties,
                        withOwnProfile: true,
                        address: this.selectedAddress,
                        distance: this.selectedDistance
                    },
                    // this.likes,
                    // this.bookmarks,
                    this.order,
                    // this.event.selectedGroup ? this.event.selectedGroup.id : null,
                    // false,
                    // this.selectedKeywordIds,
                    // this.selectedCounties
                ).subscribe(
                    (response) => {

                        this.matchingOrderAllowed = !!response['matchingOrderAllowed'];

                        if (reset) {
                            this.visibleAttendees = [];
                        }

                        response['participants'].forEach(participant => {
                            this.visibleAttendees.push(new Participant(participant));
                        });

                        // NOTE[jg] - disabled as we dont allow now to browse full list
                        // apply grouped number from licence view  only when filter is not set....
                        // used to show sum of all participants profiles for connected communities.. 
                        // now for Skillsclub
                        // if (
                        //     this.env.useLicenceTotal.includes(this.event.id) && !!this.event['participant_count']?['total']
                        //     && !this.attendeesSearch
                        //     && !this.likes
                        //     && !this.bookmarks
                        //     && this.selectedKeywordIds.length == 0
                        //     && this.selectedCounties.length == 0
                        //     && !this.selectedAddress
                        // ) {
                        //     this.attendeesTotal = this.event['participant_count']['total'];
                        // } else {
                        this.attendeesTotal = response['total'];
                        // }

                        this.pager = this.paginatorService.getPager(response['total'], this.page, this.attendeesLimit);

                        this.loading = false;
                        this.dataLoading = false;

                        // hide loading
                        if ($event) {
                            $event.target.complete();
                        }

                    },
                    (error) => {
                        console.info(error);
                        // hide loading
                        if ($event) {
                            $event.target.complete();
                        } else {
                            this.loading = false;
                            this.dataLoading = false;
                        }
                    });
            });
    }

    /**
     * reset attendee filters
     *
     * @return void
     */
    public resetAttendeeForm() {
        this.visibleAttendees = [];
        this.likes = false;
        this.bookmarks = false;
        if (this.limitedMemberList) {
            this.order = 'search';
        } else {
            this.order = 'matching_factor';
        }
        this.attendeesOffset = 0;
        this.page = 1;
        this.attendeesSearch = '';
        this.selectedKeywordIds = [];
        this.selectedCounties = [];
        this.selectedAddress = null;
        this.selectedDistance = 20;
    }

    /**
     * set actual page for attendee list
     *
     * @param {number} page
     *
     * @return void
     */
    public setAttendeePage(page: number) {

        if (!this.pager) {
            this.pager = this.paginatorService.getPager(this.attendeesTotal, 1, this.attendeesLimit);
        }
        // disable pagination for mobile
        if (!this.plt.sizeSm) {
            if (page < 1 || (page > this.pager.totalPages && this.pager.totalPages)) {
                return;
            }

            this.page = page;

            // get pager object from service
            this.pager = this.paginatorService.getPager(this.attendeesTotal, this.page, this.attendeesLimit);
            this.attendeesOffset = this.pager.startIndex;
        }

        this.refreshAttendees();
    }

    /**
     * load more attendees in infinitive scroll
     *
     * @param event
     *
     * @return void
     */
    public loadData(event) {
        this.attendeesOffset += this.attendeesLimit;
        this.refreshAttendees(event, false);
    }

    /**
     * select group to show attendees of group
     *
     * @param {Group} group
     */
    public groupSelect(group: Group) {
        if (!this.loading) {
            this.event.selectedGroup = group;
            // reset offset
            this.attendeesOffset = 0;
            this.page = 1;
            this.refresh();
            // if (this.event.id) {
            //     this.appEvents.broadcast('event:update', this.event);
            // }
        }
    }

    /**
     * select attendees tab to show attendees
     *
     * @param {AttendeesTab} tab
     */
    public attendeesTabSelect(tab?: any) {
        this.selectedAttendeesTabId = tab;

        if (!this.loading) {
            // reset offset
            this.attendeesOffset = 0;
            this.page = 1;
            this.refresh();
        }
    }

    /**
     * show popover with attendees filter
     *
     * @param type
     *
     * @return void
     */
    public changeViewType(type: string) {
        this.viewType = type;

    }

    /**
     * show popover with attendees filter
     *
     * @param event
     *
     * @return void
     */
    async showFilter(ev: any) {
        const popover = await this.popoverController.create({
            component: AttendeeFilterComponent,
            event: ev,
            cssClass: 'attendee:filter',
            componentProps: {
                'order': this.order,
                'likes': this.likes,
                'bookmarks': this.bookmarks,
                'matchingOrderAllowed': this.matchingOrderAllowed,
                'limitedMemberList': this.limitedMemberList

            }
        });

        popover.onWillDismiss().then((data) => {
            if (data.data) {
                this.afterFilterChanged(data.data);
            }
        });

        await popover.present();
    }

    public showKeywordSettings() {
        this.router.navigate([this.plt.defaultLink + '/keywords/manage-keywords']);
    }

    public showKeywordMatching() {
        this.router.navigate([this.plt.defaultLink + '/keywords/matching']);
    }

    /**
     * get enabled keywords by container and column
     */
    public getEnabledKeywords(container: Container, column) {
        return container.keywords.filter((keyword) => {
            return this.isEnabled(column, keyword);
        });
    }

    /**
     * Init block for event detail page
     *
     */
    public initBlocks() {
        this.blocks = [
            {
                key: 'description',
                title: 'DASHBOARD_EVENT_INFO_DESCRIPTION',
                opened: true
            },
            {
                key: "agenda",
                title: 'DASHBOARD_EVENT_INFO_AGENDA'
            },
            {
                key: "speaker",
                title: 'DASHBOARD_EVENT_INFO_SPEAKER'
            },
            {
                key: "partners",
                title: 'DASHBOARD_EVENT_INFO_PARTNERS'
            },
            {
                key: "other",
                title: 'DASHBOARD_EVENT_INFO_OTHER'
            },
            // {
            //     key: "directions",
            //     customLabel: {},
            //     defaultIcon: 'location.svg'
            // }
        ];
        for (let block of this.blocks) {
            // custom icons for tabs
            if (this.event.custom_labels) {
                this.event.custom_labels.forEach((customLabel) => {
                    if (block.key == customLabel.key && (customLabel.icon || customLabel.translations.length > 0)) {
                        block.customLabel = customLabel;
                        if (block.customLabel.translate(this.userLang, this.event.default_locale).name) {
                            block.title = block.customLabel.translate(this.userLang, this.event.default_locale).name;
                        }
                    }
                });
            }
        }

        // TODO
        // if (this.event.custom_labels) {
        //     this.event.custom_labels.forEach((customLabel) => {
        //         if (customLabel.key == 'directions') {
        //             this.customLabelsDirection = customLabel
        //         }
        //     });
        // }
    }

    /**
     * Method to change visible segment
     *
     * @param event
     */
    public tabChanged(segment, view, scrollTop = false, matchingRefresh = false) {
        this.segment = segment;
        this.view = view;

        this.router.navigate([this.plt.defaultLink + '/home/' + this.segment + '/' + (this.view ? this.view : '')]);

        this.dataLoading = true;
        if (matchingRefresh) {
            this.savedKeywords = view != 'manage-keywords';
            if (this.savedKeywords) {
                // NOTE[jg] - this is done in routing events now
                // this.loadMostMatching(null, true);
            } else {
                if (this.plt.sizeSm && this.scrollTop) {
                    setTimeout(() => {
                        this.content.scrollToPoint(0, this.scrollTop, 0);
                        this.dataLoading = false;
                    }, 150);
                }
            }
        } else {
            // NOTE[jg] - this is done in routing events now
            // this.resetAttendeeForm();
            // this.refresh();
        }

        if (scrollTop) {
            this.content.scrollToTop(0);
        }

        // TODO[jg] - check this...
        setTimeout(() => {
            this.frameHeight = (window.innerHeight - 300);
            if (this.frameHeight < 650) {
                this.frameHeight = 650;
            }
        }, 500);
    }

    /**
     * Method to change visible block in event detail
     *
     * @param keyword
     */
    public blockChanged(blockSelected) {
        let original = blockSelected.opened;
        for (let block of this.blocks) {
            block.opened = false;
        }
        blockSelected.opened = !original;
    }

    /**
     * mark external content as loaded
     *
     * @param key
     */
    public onLoad(key: String) {
        setTimeout(() => {
            this.blocks.forEach((block, i) => {
                if (block.key == key) {
                    block.customLabel.loadedIframe = true
                }
            });
        }, 500);
    }

    logScrolling(e) {
        if (this.plt.sizeSm && !this.dataLoading) {
            this.scrollTop = e.detail.scrollTop;
        }
        if (e.detail.scrollTop > 86 && !this.plt.hideDashboardHeader) {
            this.showHeader = true;
        } else {
            this.showHeader = false;
        }
    }

    /**
     * open event detail popup
     *
     * @return void
     */
    public async showEventDetail(block) {
        if (block.customLabel
            && block.customLabel.translate(this.userLang, this.event.default_locale).external_link
            && !this.event.translate(this.userLang)[block.key]) {

            this.plt.showExternalLink(block, this.userLang, this.event.default_locale);

        } else {

            this.routerExtService.softNavigate();

            const modal = await this.modalController.create({
                component: EventDetailComponent,
                cssClass: 'event-modal',
                componentProps: {
                    block: block,
                }
            });

            modal.onWillDismiss().then((data) => {
                if (data.data?.action == 'close') {
                    this.routerExtService.softBack();
                }
            });

            return await modal.present();
        }
    }

    /**
     * open notifications popup
     *
     * @return void
     */
    public async openNotifications() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: NotificationsComponent,
            cssClass: 'attendee-modal',
            componentProps: {
                event: this.event
            },
            id: 'notifications'
        });

        modal.onWillDismiss().then((data) => {
            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }
        });

        return await modal.present();
    }

    /**
     * set attendee limit values
     *
     * @return void
     */
    private setAttendeesLimitValues() {
        this.attendeesLimitValues = [
            // {
            //     value: 10,
            //     name: "10"
            // },
            {
                value: 40,
                name: "40"
            },
            {
                value: 80,
                name: "80"
            },
            {
                value: 160,
                name: "160"
            },
            // {
            //     value: 320,
            //     name: "320"
            // },
            // {
            //     value: 500,
            //     name: "500"
            // },
            // {
            //     value: 1000,
            //     name: this.translate.instant('ATTENDEES_PAGINATOR_SHOW_ALL')
            // }
        ];
    }

    /**
     * open appointment/personal appointment detail
     * @param Appointment $appointment
     * @return string
     */
    public openAppointmentLink(appointment) {
        if (this.homeUrlSegment == 'home') {
            if (appointment.status != 4) {
                this.router.navigate([this.plt.defaultLink + '/appointments/' + appointment.id]);
            } else {
                this.router.navigate([this.plt.defaultLink + '/appointments', { 'starts_at': appointment.starts_at }]);
            }
        }

        return false;
    }

    /**
     * clear the community filters
     * @return void
     */
    public clearExtendedFilter() {
        this.attendeesSearch = '';
        this.selectedKeywordIds = [];
        this.selectedCounties = [];
        this.selectedDistance = 20;
        this.selectedAddress = null;
        this.likes = false;
        this.bookmarks = false;
        this.filtered = false;
        this.showSearchForm = true;
        if (!this.limitedMemberList) {
            this.refreshAttendees();
        }
    }

    /**
     * open community attendees filter
     *
     * @return void
     */
    public async showCommunityFilter() {

        if (this.limitedMemberList) {
            this.showSearchForm = !this.showSearchForm;
        } else {

            this.routerExtService.softNavigate();

            const modal = await this.modalController.create({
                component: CommunityFilterComponent,
                cssClass: 'community-filter-modal',
                componentProps: {
                    attendeeSearch: this.attendeesSearch,
                    selectedKeywordIds: this.selectedKeywordIds,
                    selectedCounties: this.selectedCounties,
                    selectedAddress: this.selectedAddress,
                    selectedDistance: this.selectedDistance
                }
            });

            modal.onWillDismiss().then((data) => {
                if (data.data) {
                    if (data.data.attendeesSearch) {
                        this.attendeesSearch = data.data.attendeesSearch;
                    } else {
                        this.attendeesSearch = ''
                    }

                    if (data.data.selectedKeywordIds) {
                        this.selectedKeywordIds = data.data.selectedKeywordIds;
                    } else {
                        this.selectedKeywordIds = [];
                    }

                    if (data.data.selectedCounties) {
                        this.selectedCounties = data.data.selectedCounties;
                    } else {
                        this.selectedCounties = [];
                    }

                    if (data.data.selectedAddress) {
                        this.selectedAddress = data.data.selectedAddress;
                    } else {
                        this.selectedAddress = '';
                    }

                    if (data.data.selectedDistance) {
                        this.selectedDistance = data.data.selectedDistance;
                    } else {
                        this.selectedDistance = 20;
                    }

                    if (data.data?.action == 'close') {
                        this.routerExtService.softBack();
                    }

                    this.setAttendeePage(1);
                }
            });

            return await modal.present();
        }
    }

    /**
     * Apply filter from local component
     *
     * @return void
     */
    public applyFilter(filterOptions) {


        this.visibleAttendees = [];

        this.filtered = false;

        if (filterOptions.attendeesSearch) {
            this.attendeesSearch = filterOptions.attendeesSearch;
            this.filtered = true;
        } else {
            this.attendeesSearch = ''
        }

        if (filterOptions.selectedKeywordIds?.length > 0) {
            this.selectedKeywordIds = filterOptions.selectedKeywordIds;
            this.filtered = true;
        } else {
            this.selectedKeywordIds = [];
        }

        if (filterOptions.selectedCounties?.length > 0) {
            this.selectedCounties = filterOptions.selectedCounties;
            this.filtered = true;
        } else {
            this.selectedCounties = [];
        }

        if (filterOptions.selectedAddress) {
            this.selectedAddress = filterOptions.selectedAddress;
            this.filtered = true;
        } else {
            this.selectedAddress = '';
        }

        if (filterOptions.selectedDistance) {
            this.selectedDistance = filterOptions.selectedDistance;
        } else {
            this.selectedDistance = 20;
        }

        if (this.filtered) {
            this.setAttendeePage(1);
            this.showSearchForm = false;
        }
    }

    public changeLoading(value: boolean) {
        this.loading = value;
    }

    public changeDataLoading(value: boolean) {
        this.dataLoading = value;
    }

    public newsfeedSended() {
        this.newsfeedListComponent.refresh();
    }

    public editNewsfeed(newsfeed: Newsfeed) {
        this.addNewsfeedComponent.editNewsfeed(newsfeed);
    }

    /**
     * open modal with newsfeed add form
     * @return void
     */
    public async addNewsfedd() {

        this.routerExtService.softNavigate();

        const modal = await this.modalController.create({
            component: AddNewsfeedComponent,
            cssClass: 'newsfeed-form',
            componentProps: {
                modal: true,
                displayBigTextarea: true,
                showKeywords: this.event.matching_status && !this.keywordsEmpty,
                event: this.event,
                user: this.user
            }
        });

        modal.onWillDismiss().then((data) => {

            if (data.data) {
                this.newsfeedListComponent.refresh()
            }

            if (data.data?.action == 'close' || data.role == 'backdrop') {
                this.routerExtService.softBack();
            }

        });

        return await modal.present();
    }

    public addMarketplacePost() {
        this.marketplaceList.createMarketplacePost();
    }

    /**
     * load newsfeed detail and open modal if possible
     *
     * @return modal
     */
    public async showNewsfeedDetail(newsfeedId: number) {
        if (!this.feedDetailModalOpened) {
            this.feedDetailModalOpened = true;
            this.newsfeedService.getById(newsfeedId).subscribe(
                (newsfeed: Newsfeed) => {
                    this.openNewsfeedDetail(new Newsfeed(newsfeed));
                },
                (error) => {
                    this.plt.newsfeedOpened = false;
                    this.feedDetailModalOpened = false;
                    // show error
                    this.overlayService.showError(error.error.message);
                });
        }
    }

    /**
     * open newsfeed detail with comments in new modal
     *
     * @return modal
     */
    public async openNewsfeedDetail(newsfeed: Newsfeed, direct: boolean = false) {

        // direct opening
        if (direct) {
            this.routerExtService.softNavigate();
        } else {
            // update url
            let link = this.routerExtService.currentUrl.split("?")[0] + '?newsfeed_id=' + newsfeed.id.toString();
            // add event id also
            link += '&event_id=' + this.event.id.toString();
            this.routerExtService.softNavigate(link);
        }

        newsfeed.commentsVisible = true;

        const detail = await this.modalController.create({
            component: NewsfeedDetailComponent,
            cssClass: 'newsfeed-detail',
            componentProps: {
                newsfeed: newsfeed,
                event: this.event,
                modal: true
            }
        });

        detail.onWillDismiss().then((data) => {

            this.feedDetailModalOpened = false;
            this.plt.newsfeedOpened = false;

            if (data) {
                if (data.data?.keywords) {
                    // TODO[jg] refresh details about comments and so...
                }
                if (data.data?.action == 'close' || data.role == 'backdrop') {

                    if (direct) {
                        this.routerExtService.softBack();
                    } else {
                        let link = this.plt.defaultLink + this.routerExtService.currentUrl.split("?")[0].split("#")[0].split("#")[0].replace('/tabs', '');
                        if (this.plt.tabMenu && !this.routerExtService.currentUrl.includes('/tabs')) {
                            this.router.navigateByUrl(link);
                        } else {
                            this.routerExtService.softNavigate(link);
                        }
                    }

                } else {
                    // do nothing for back navigation from browser..
                    // just close modal and update history
                    this.routerExtService.popHistory();
                }

                // delete event from opened modal
                if (data.data?.deleteId) {
                    // remove this notification....
                }
            }
        });

        return await detail.present();
    }


    /**
     * Check, if the user has activated a keyword for a specific column
     *
     * @param column
     * @param keyword
     * @returns {boolean}
     */
    public isEnabled(column: number, keyword: Keyword): boolean {
        if (this.user && this.user.selected_participant) {
            for (let k = 0; k < this.user.selected_participant.keywords.length; k++) {
                const item = this.user.selected_participant.keywords[k];
                if (keyword.id === item.id && column === item.pivot.column) {
                    return true;
                }
            }
        }

        return false;
    }

    openRecommendation($event) {
        $event.stopPropagation();
        let activeShare = this.event.recommendation.setting.filter((share) => share.enabled == true);

        if (activeShare.length == 1) {
            this.share(activeShare[0].type)
        } else {
            this.showSharing = !this.showSharing;
        }
    }

    share(type) {
        this.showSharing = false;
        let share = { ...this.event.recommendation };
        this.plt.shareLink(type, share);
    }

    // function to close all modals
    public dismissModals() {
        this.modalController.getTop().then((modal) => {
            if (modal) {
                this.modalController.dismiss().then(() => {
                    this.dismissModals();
                });
            }
        });
    }

    /**
     * on destroy
     *
     * @return void
     */
    ngOnDestroy() {

        if (this.subscription) {
            this.subscription.unsubscribe();
        }

        if (this.subscriptionEvent) {
            this.subscriptionEvent.unsubscribe();
        }

        // we close all subscription connected to this page
        if (this.eventChangeHandler) {
            this.appEvents.unsubscribe('event:changed', this.eventChangeHandler);
        }
        if (this.attendeeDetailHandler) {
            this.appEvents.unsubscribe('attendee:changed', this.attendeeDetailHandler);
        }
        if (this.filterHandler) {
            this.appEvents.unsubscribe('attendee:filter', this.filterHandler);
        }
        if (this.attendeeShowHandler) {
            this.appEvents.unsubscribe('attendee:show', this.attendeeShowHandler);
        }
        if (this.newsfeedShowHandler) {
            this.appEvents.unsubscribe('newsfeed:show', this.newsfeedShowHandler);
        }
        if (this.marketplacePostShowHandler) {
            this.appEvents.unsubscribe('marketplace:post:show', this.marketplacePostShowHandler);
        }
        if (this.votingShowHandler) {
            this.appEvents.unsubscribe('voting:show', this.votingShowHandler);
        }
        if (this.videoShowHandler) {
            this.appEvents.unsubscribe('video:show', this.videoShowHandler);
        }

        // close all active modals when leaving page
        this.overlayService.closeModals();
        this.ngUnsubscribe.next();
        this.ngUnsubscribe.complete();
    }
}
